/**
 * The booking a logged-out visitor put together, kept across the sign-in detour.
 *
 * Picking a date, a time and a price variant is the most expensive work we ask a visitor to do,
 * and until now every bit of it was thrown away the moment they pressed "Buchen" without an
 * account: the order panel submitted, the auth guard in `Routes.js` bounced them to /login or
 * /signup with nothing but a `from` path, and they came back to an empty checkout. The
 * soft-booking page — the main B2C flow — was worse than empty: it reads its payload from
 * `history.location.state`, which `NamedRedirect` does not carry, so it rendered "–" for the
 * dates and a submit button that could not work.
 *
 * Two separate records live here, because they have different lifetimes and different owners:
 *
 *   1. The *resume intent* (`savePendingBooking`) — the raw order-panel form values, written only
 *      when a logged-out visitor presses the CTA, replayed once they are signed in, then deleted.
 *      Short-lived on purpose: it drives an automatic navigation, and an automatic navigation the
 *      visitor has forgotten about is a bug, not a feature.
 *   2. The *soft-booking payload* (`saveSoftBookingData`) — what SoftBookingCheckoutPage needs to
 *      render and submit. Written on every soft-booking push, signed in or not, purely so that
 *      page can survive losing its history state. It never triggers navigation, so it may live
 *      longer, and the page keeps it until the booking succeeds — dropping it on arrival made a
 *      recovered checkout a one-shot that any reload turned into a dead end. What stops a stale
 *      record coming back as a live checkout is the page's own test that the booking window has
 *      not already passed, not the lifetime of this record.
 *
 * sessionStorage, not Redux: a social sign-in is a full round-trip to the identity provider and
 * back, which wipes the store. sessionStorage is also per-tab, which is what we want — two tabs
 * shopping two spaces must not resume into each other — and `src/reducers.js` clears all of it on
 * logout, so a record cannot survive into another user's session. A browser that refuses storage
 * gets no booking memory at all, by design: see the comment on write().
 */
import { types as sdkTypes } from './sdkLoader';

const PENDING_BOOKING_KEY = 'cowork24-pending-booking';
const SOFT_BOOKING_KEY = 'cowork24-soft-booking-checkout';

/**
 * How long a stored intent may still be replayed automatically.
 *
 * An hour, not the 15 minutes this started at. The window is no longer what protects against a
 * forgotten booking being replayed — two sharper guards do that now: dismissing the dialog on the
 * listing forgets it immediately, and so does signing in anywhere that is neither the listing nor
 * the sign-in journey itself. What the window is left protecting against is narrow, so it should
 * not also be the thing that fails an honest visitor.
 *
 * And creating an account is slower than it looks: pick a user type, first and last name, e-mail,
 * a password that satisfies the rules, the terms checkbox, any custom fields the marketplace adds,
 * and often a second attempt after a validation error. A window that expires mid-form does not warn
 * anybody — the visitor simply signs up, lands back on the listing, and nothing happens.
 */
export const RESUME_MAX_AGE_MS = 60 * 60 * 1000;

/**
 * How long SoftBookingCheckoutPage may still restore itself from storage. Module-private: it is a
 * tuning knob, not part of the contract. Only ever re-renders a
 * page the visitor is already looking at, and the page consumes the record on arrival, so the
 * window only has to cover the redirect that lost the history state.
 */
const RESTORE_MAX_AGE_MS = 60 * 60 * 1000;

// Dates are the whole point of the record, and JSON has no notion of them. Same shape as
// CheckoutPageSessionHelpers uses, so the two stores stay readable in the same way; SDK types
// (Money, UUID) ride along on the SDK's own replacer/reviver for the soft-booking payload.
const replacer = function(k, v) {
  if (this[k] instanceof Date) {
    return { date: v, _serializedType: 'SerializableDate' };
  }
  return sdkTypes.replacer(k, v);
};

const reviver = (k, v) => {
  if (v && typeof v === 'object' && v._serializedType === 'SerializableDate') {
    return new Date(v.date);
  }
  return sdkTypes.reviver(k, v);
};

const storage = () => {
  try {
    // Chrome with "block all cookies" throws a SecurityError on the *property access* itself — it
    // does not return null — so even reading this has to be inside a try.
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

// Deliberately no in-memory fallback for a browser that refuses storage. A module-scope object
// would be a second tier that read() and write() have to keep in sync (they did not: a failed
// setItem left the older generation in storage, and read() prefers storage, so the store served
// the previous listing's booking), it would survive the sessionStorage.clear() that src/reducers.js
// does on logout — carrying one user's booking into the next user's session in the same tab — and
// on the server it would be a process-global shared by every concurrent request. When storage is
// refused, savePendingBooking reports the failure and handleSubmit falls back to the plain
// navigation, which the auth guard now carries state through anyway.
const write = (key, payload) => {
  const store = storage();
  if (!store) {
    return false;
  }
  try {
    store.setItem(key, JSON.stringify({ ...payload, savedAt: new Date() }, replacer));
    return true;
  } catch (e) {
    // Quota, private mode.
    return false;
  }
};

const read = (key, maxAgeMs) => {
  const store = storage();
  if (!store) {
    return null;
  }

  let parsed = null;
  try {
    const raw = store.getItem(key);
    parsed = raw ? JSON.parse(raw, reviver) : null;
  } catch (e) {
    parsed = null;
  }
  if (!parsed) {
    return null;
  }

  const { savedAt, ...rest } = parsed;
  const age = savedAt instanceof Date ? Date.now() - savedAt.getTime() : Infinity;
  // A negative age means the clock moved backwards under us; treat it as expired rather than as
  // fresh forever.
  if (!(age >= 0 && age <= maxAgeMs)) {
    remove(key);
    return null;
  }
  return rest;
};

const remove = key => {
  const store = storage();
  if (!store) {
    return;
  }
  try {
    store.removeItem(key);
  } catch (e) {
    // Nothing we can do, and nothing worth breaking a booking over.
  }
};

/**
 * Whether `pathname` is the listing page a booking intent belongs to.
 *
 * One rule, used by everything that has to answer this: the resume, the Topbar's post-sign-in
 * cleanup and the login dialog's "your selection is saved" panel. A substring test is not enough —
 * the same uuid appears in `/l/:slug/:id/checkout`, `/pending-approval` and `/draft`, none of which
 * can replay an intent, so matching those made the dialog promise a resume that never came.
 *
 * @param {string} pathname
 * @param {string} listingId
 * @returns {boolean}
 */
export const isListingPathFor = (pathname, listingId) =>
  !!listingId &&
  typeof pathname === 'string' &&
  pathname.replace(/\/+$/, '').endsWith(`/${listingId}`);

/**
 * Whether dismissing the login dialog should also forget the booking.
 *
 * Only when the visitor is still standing on the listing the dialog was opened from. Clicking
 * "create an account" ALSO removes the dialog's URL param — it navigates to /signup — and reading
 * that as "they said no" deleted the intent of every visitor who took the one path the dialog
 * exists to offer. Signing in with a password resolves in place and keeps the pathname, which is
 * why that flow worked and creating an account never did.
 *
 * @param {Object} params
 * @param {Object|null} params.pending
 * @param {string} params.pathname
 * @returns {boolean}
 */
export const shouldClearOnDialogDismissed = ({ pending, pathname }) =>
  !pending || isListingPathFor(pathname, pending.listingId);

/**
 * Whether a sign-in that just completed means the booking is no longer what the visitor is after.
 *
 * True only when they are signed in, somewhere that is neither the listing nor part of the sign-in
 * journey itself. /login, /signup and the confirm step are mid-journey: signing up completes ON
 * /signup and only redirects back to the listing on the next render, so treating that as "signed in
 * somewhere else" throws away the booking the account was just created for.
 *
 * @param {Object} params
 * @param {Object|null} params.pending
 * @param {string} params.pathname
 * @param {boolean} params.isOnAuthPage
 * @returns {boolean}
 */
export const shouldClearAfterSignIn = ({ pending, pathname, isOnAuthPage }) =>
  !!pending && !isOnAuthPage && !isListingPathFor(pathname, pending.listingId);

/**
 * Remember what a logged-out visitor was about to book.
 *
 * @param {Object} params
 * @param {string} params.listingId - UUID string of the listing being booked
 * @param {Object} params.values - the order panel's raw form values, replayed verbatim later
 * @param {string} [params.listingTitle] - shown in the login dialog, so the visitor can see that
 *   the selection is still there
 * @param {Date} [params.bookingStart] - display value; for day-based units this is the inclusive
 *   end the picker showed, not the exclusive one the API takes
 * @param {Date} [params.bookingEnd] - display value, see bookingStart
 * @param {string} [params.timeZone] - the listing's IANA zone, so the dialog shows the same
 *   wall-clock time the order panel did
 * @param {number} [params.seats]
 * @param {boolean} [params.isDayBased] - render the range as dates only, without times
 * @returns {boolean} whether the intent was stored
 */
export const savePendingBooking = ({ listingId, values, ...summary }) => {
  if (!listingId || !values) {
    return false;
  }
  return write(PENDING_BOOKING_KEY, { listingId, values, ...summary });
};

/**
 * The stored intent, or null when there is none, it has expired, or it cannot be read.
 *
 * Validates the one field the resume actually feeds back into the app: a record without usable
 * `values` would throw out of the effect that replays it and take the listing page down with it.
 *
 * @returns {{listingId: string, values: Object}|null}
 */
export const readPendingBooking = () => {
  const stored = read(PENDING_BOOKING_KEY, RESUME_MAX_AGE_MS);
  const isUsable =
    !!stored &&
    typeof stored.listingId === 'string' &&
    !!stored.values &&
    typeof stored.values === 'object';
  if (!isUsable) {
    if (stored) {
      clearPendingBooking();
    }
    return null;
  }
  return stored;
};

/**
 * Forget the intent. Called when it has been replayed, when the visitor dismisses the login
 * dialog, and when they pick a different action on the listing (contacting the host).
 */
export const clearPendingBooking = () => remove(PENDING_BOOKING_KEY);

/**
 * Everything SoftBookingCheckoutPage needs to render and submit, so it no longer depends on
 * history state it can lose.
 *
 * @param {Object} data - listingId is required; bookingStart/bookingEnd/seats/price/listingTitle
 *   are what the page renders and submits
 * @returns {boolean} whether the payload was stored
 */
export const saveSoftBookingData = data => {
  if (!data?.listingId) {
    return false;
  }
  return write(SOFT_BOOKING_KEY, data);
};

/**
 * The stored soft-booking payload, or null when there is none or it has expired.
 *
 * @returns {Object|null}
 */
export const readSoftBookingData = () => read(SOFT_BOOKING_KEY, RESTORE_MAX_AGE_MS);

/**
 * Whether a soft-booking payload is young enough to still open a live checkout.
 *
 * The storage mirror gets this for free — `read()` drops a record older than RESTORE_MAX_AGE_MS.
 * The copy in `history.location.state` does not: history entries have no lifetime, so pressing Back
 * far enough re-mounts SoftBookingCheckoutPage with a payload from days ago and it arms a real
 * checkout on it. On the BOOKING track the window test hides that (a booking whose end has passed
 * is refused anyway); on the PURCHASE track there is no window to test, only a quantity, so nothing
 * stopped it.
 *
 * `savedAt` is stamped into the payload by ListingPage's handleSubmit. Two things legitimately
 * arrive without one and must stay usable:
 *   - the storage mirror, whose own `savedAt` lives in the envelope and is stripped by `read()`,
 *     having already served as the TTL there;
 *   - history entries created before this field existed.
 * So: absent means "not ours to judge", and only a stamp we can actually read can fail. A stamp
 * from the future fails too — that is a clock that moved backwards, not a fresh payload.
 *
 * @param {number} [savedAt] epoch milliseconds
 * @returns {boolean}
 */
export const isSoftBookingPayloadFresh = savedAt => {
  if (!Number.isFinite(savedAt)) {
    return true;
  }
  const age = Date.now() - savedAt;
  return age >= 0 && age <= RESTORE_MAX_AGE_MS;
};

/**
 * Forget the soft-booking payload. Called once a booking has actually been created; until then the
 * record is what lets the checkout survive a reload.
 */
export const clearSoftBookingData = () => remove(SOFT_BOOKING_KEY);
